
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Button from '../components/Button';

// Types for the active test session
interface Question {
    id: number;
    text: string;
    options: string[];
    correctAnswer: string;
    domain: string;
    explanation: string;
}

interface ActiveTestSession {
    blueprintId: string;
    title: string;
    questions: Question[];
}

const ActiveTestPage: React.FC = () => {
    const navigate = useNavigate();
    const [session, setSession] = useState<ActiveTestSession | null>(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [flagged, setFlagged] = useState<Record<number, boolean>>({});
    const [timeElapsed, setTimeElapsed] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load session
    useEffect(() => {
        const stored = localStorage.getItem('activeTest');
        if (!stored) {
            alert("No active test found!");
            navigate('/tests');
            return;
        }
        setSession(JSON.parse(stored));
    }, [navigate]);

    // Timer
    useEffect(() => {
        const timer = setInterval(() => setTimeElapsed(t => t + 1), 1000);
        return () => clearInterval(timer);
    }, []);

    if (!session) return <div className="min-h-screen bg-slate-50 flex items-center justify-center">Loading...</div>;

    const currentQuestion = session.questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === session.questions.length - 1;

    const handleAnswer = (option: string) => {
        setAnswers({ ...answers, [currentQuestionIndex]: option });
    };

    const toggleFlag = () => {
        setFlagged({ ...flagged, [currentQuestionIndex]: !flagged[currentQuestionIndex] });
    };

    const handleSubmit = async () => {
        const unanswered = session.questions.length - Object.keys(answers).length;
        if (unanswered > 0 && !confirm(`You have ${unanswered} unanswered questions. submitting now will mark them as incorrect. Continue?`)) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Calculate score purely for display, backend will verify/re-calc
            // Just sending raw answers is better
            let score = 0;
            const answerArray = session.questions.map((q, idx) => {
                const ans = answers[idx];
                if (ans === q.correctAnswer) score++;
                return ans || '';
            });

            const payload = {
                blueprintId: session.blueprintId,
                totalQuestions: session.questions.length,
                timeTakenSeconds: timeElapsed,
                answers: answerArray,
                questions: session.questions, // Send back so backend knows domains/answers (stateless mostly)
                score: score
            };

            const result = await api.post('/tests/submit', payload);

            // Clear session
            localStorage.removeItem('activeTest');

            // Navigate to results (we could create a dedicated results page, but for now alert and go back)
            alert(`Test Submitted!\nScore: ${score}/${session.questions.length}\nPoints Earned: ${result.pointsEarned}`);
            navigate('/tests');

        } catch (err) {
            alert('Failed to submit test. Please check your connection.');
            setIsSubmitting(false);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col">
            {/* Header / Toolbar */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center sticky top-0 z-10 shadow-sm">
                <div>
                    <h2 className="font-bold text-slate-800">{session.title}</h2>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">
                        Question {currentQuestionIndex + 1} of {session.questions.length}
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    <div className="flex items-center bg-slate-100 px-3 py-1 rounded-lg">
                        <i className="fas fa-stopwatch mr-2 text-slate-400"></i>
                        <span className="font-mono font-bold text-slate-700">{formatTime(timeElapsed)}</span>
                    </div>

                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={isSubmitting ? 'opacity-50' : ''}
                    >
                        Finish Test
                    </Button>
                </div>
            </div>

            <div className="flex-grow flex p-4 gap-4 max-w-7xl mx-auto w-full">
                {/* Sidebar Navigation */}
                <div className="w-64 bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex-shrink-0 hidden lg:block overflow-y-auto max-h-[calc(100vh-100px)]">
                    <h3 className="text-xs font-bold text-slate-400 uppercase mb-4">Navigator</h3>
                    <div className="grid grid-cols-4 gap-2">
                        {session.questions.map((q, idx) => {
                            const isAnswered = answers[idx] !== undefined;
                            const isCurrent = idx === currentQuestionIndex;
                            const isFlagged = flagged[idx];

                            return (
                                <button
                                    key={q.id}
                                    onClick={() => setCurrentQuestionIndex(idx)}
                                    className={`
                                        aspect-square rounded-lg text-sm font-bold flex items-center justify-center relative
                                        ${isCurrent ? 'bg-indigo-600 text-white shadow-md ring-2 ring-indigo-200' :
                                            isAnswered ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'}
                                    `}
                                >
                                    {idx + 1}
                                    {isFlagged && <div className="absolute top-0 right-0 w-2 h-2 bg-orange-500 rounded-full -mr-1 -mt-1 ring-2 ring-white"></div>}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Main Question Area */}
                <div className="flex-grow bg-white rounded-2xl shadow-sm border border-slate-200 p-8 flex flex-col">
                    <div className="flex justify-between items-start mb-6">
                        <span className="bg-slate-100 text-slate-500 px-3 py-1 rounded-full text-xs font-bold uppercase">
                            {currentQuestion.domain}
                        </span>
                        <button
                            onClick={toggleFlag}
                            className={`text-sm font-bold flex items-center gap-2 transition-colors ${flagged[currentQuestionIndex] ? 'text-orange-500' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <i className={`fas fa-flag ${flagged[currentQuestionIndex] ? 'fas' : 'far'}`}></i>
                            {flagged[currentQuestionIndex] ? 'Flagged for Review' : 'Flag for Review'}
                        </button>
                    </div>

                    <h1 className="text-2xl font-bold text-slate-800 mb-8 leading-relaxed">
                        {currentQuestion.text}
                    </h1>

                    <div className="space-y-4 max-w-3xl">
                        {currentQuestion.options.map((option, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleAnswer(option)}
                                className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center group ${answers[currentQuestionIndex] === option
                                        ? 'border-indigo-600 bg-indigo-50 text-indigo-900 shadow-sm'
                                        : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                                    }`}
                            >
                                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-4 font-bold transition-colors ${answers[currentQuestionIndex] === option
                                        ? 'border-indigo-600 bg-indigo-600 text-white'
                                        : 'border-slate-300 text-slate-400 group-hover:border-indigo-300'
                                    }`}>
                                    {String.fromCharCode(65 + idx)}
                                </div>
                                <span className="text-lg">{option}</span>
                            </button>
                        ))}
                    </div>

                    <div className="mt-auto pt-8 flex params-center justify-between border-t border-slate-100 mt-8">
                        <Button
                            variant="outline"
                            onClick={() => setCurrentQuestionIndex(i => Math.max(0, i - 1))}
                            disabled={currentQuestionIndex === 0}
                        >
                            <i className="fas fa-arrow-left mr-2"></i> Previous
                        </Button>

                        {isLastQuestion ? (
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                Submit Test <i className="fas fa-check ml-2"></i>
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                onClick={() => setCurrentQuestionIndex(i => Math.min(session.questions.length - 1, i + 1))}
                            >
                                Next Question <i className="fas fa-arrow-right ml-2"></i>
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActiveTestPage;
